// TODO(FD) remove Q usage (this can be done with pure promises)
// TODO(FD) switch everyone to RequestBuilder, remove old functions using Q & Qwest

import Qwest from "qwest";

import Q from "q";
import _ from "lodash";
import { buildRequest, getTimestampForQueryAsObject, makeCallTreeHeader } from "../utils/RequestUtil";
import PerformanceMeasureActions from "../actions/PerformanceMeasureActions";
type HttpMethod = "get" | "post" | "put" | "delete";

// Determine whether a retry should be attempted. Returns a Promise so we can wait between retries, e.g. with exponential backoff; if no retry should be attempted, the Promise should reject with the original error.
type RetryPolicy = (error: RequestError) => Promise<void>;

const NO_RETRIES = (e: any) => {
    throw e;
};

const ALWAYS_RETRY = () => Promise.resolve();

function exponentialBackoff(policy: RetryPolicy): RetryPolicy {
    return (e) =>
        policy(e).then(() => {
            const timeout = Math.min(Math.pow(2, e.retries + 1), 16);
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve();
                }, timeout * 1000);
            });
        });
}

function maxRetries(policy: RetryPolicy, n: number): RetryPolicy {
    return (e) => {
        if (e.retries >= n) {
            throw e;
        } else {
            return policy(e);
        }
    };
}

function onlyRetriableStatus(policy: RetryPolicy): RetryPolicy {
    return (e) => {
        if (e.status !== 503 && e.status !== 504) {
            throw e;
        } else {
            return policy(e);
        }
    };
}

class Builder {
    vertical: string;
    service: string;
    version: string | undefined | null;
    template: string | undefined | null;
    pathParams:
        | {
              [x: string]: string | number;
          }
        | undefined
        | null;
    queryParams:
        | {
              [x: string]: unknown;
          }
        | undefined
        | null;
    headerParams:
        | {
              [x: string]: string;
          }
        | undefined
        | null;
    requestParams: unknown | undefined | null;
    httpMethod: HttpMethod | undefined;
    retryPolicy: RetryPolicy;
    manualBaseUrl: string | null;

    constructor(vertical: string, service: string) {
        this.vertical = vertical;
        this.service = service;
        this.version = "v1";
        this.manualBaseUrl = null;
        this.retryPolicy = NO_RETRIES;
    }

    withManualBaseUrl(manualBaseUrl: string) {
        const clone = _.clone(this);
        clone.manualBaseUrl = manualBaseUrl;
        clone.version = undefined;
        return clone;
    }

    path(
        template: string,
        pathParams: {
            [x: string]: string | number;
        }
    ): Builder {
        const clone = _.clone(this);
        clone.template = template;
        clone.pathParams = pathParams;
        return clone;
    }

    query(queryParams: { [x: string]: unknown }): Builder {
        const clone = _.clone(this);
        clone.queryParams = queryParams;
        return clone;
    }

    noVersion(): Builder {
        const clone = _.clone(this);
        clone.version = undefined;
        return clone;
    }

    header(headerParams: { [x: string]: string }): Builder {
        const clone = _.clone(this);
        clone.headerParams = headerParams;
        return clone;
    }

    body(bodyParams: unknown): Builder {
        const clone = _.clone(this);
        clone.requestParams = bodyParams;
        return clone;
    }

    method(method: HttpMethod): Builder {
        const clone = _.clone(this);
        clone.httpMethod = method;
        return clone;
    }

    retry(n: number = 2): Builder {
        const clone = _.clone(this);
        clone.retryPolicy = maxRetries(onlyRetriableStatus(exponentialBackoff(ALWAYS_RETRY)), n);
        return clone;
    }

    get(): Promise<any> {
        return promiseWithMonitoring(this.method("get").build());
    }

    post(): Promise<any> {
        return promiseWithMonitoring(this.method("post").build());
    }

    put(): Promise<any> {
        return promiseWithMonitoring(this.method("put").build());
    }

    delete(): Promise<unknown> {
        return promiseWithMonitoring(this.method("delete").build());
    }

    build(): RequestParams {
        return new RequestParams(this);
    }
}

class RequestParams {
    vertical: string;
    service: string;
    version: string | undefined | null;
    template: string;
    pathParams: {
        [x: string]: string | number;
    };
    queryParams:
        | {
              [x: string]: unknown;
          }
        | undefined
        | null;
    headerParams:
        | {
              [x: string]: string;
          }
        | undefined
        | null;
    requestParams: unknown | undefined | null;
    httpMethod: HttpMethod;
    retryPolicy: RetryPolicy;
    manualBaseUrl: string | null;

    constructor(build: Builder) {
        if (!build.httpMethod) {
            throw new Error("No HTTP method provided");
        }
        if (!build.pathParams || !build.template) {
            throw new Error("No path provided");
        }
        this.vertical = build.vertical;
        this.service = build.service;
        this.version = build.version;
        this.template = build.template;
        this.pathParams = build.pathParams;
        this.queryParams = build.queryParams;
        this.headerParams = build.headerParams;
        this.requestParams = build.requestParams;
        this.httpMethod = build.httpMethod;
        this.retryPolicy = build.retryPolicy;
        this.manualBaseUrl = build.manualBaseUrl;
    }

    static Builder = Builder;
}

const successWrapper = function (deferred: any) {
    return function (response: any) {
        deferred.resolve(response);
    };
};

class RequestError extends Error {
    status?: number;
    response: unknown;
    responseType?: string;
    responseText?: string;
    url?: string;
    callTreeId: string;
    retries: number;
    _isRequestError = true; // instanceof doesn't work correctly with babel-generated classes deriving from Error, so this is a workaround

    constructor(
        description: string | undefined,
        callTreeId: string,
        xhr: XMLHttpRequest | undefined | null | Response,
        responseText?: string,
        retries?: number
    ) {
        super(description);
        if (xhr instanceof XMLHttpRequest) {
            this.status = xhr.status;
            this.response = xhr.response;
            this.responseType = xhr.responseType;
            if (
                xhr.responseType === "" ||
                xhr.responseType === "document" ||
                (xhr.responseType as string) === "moz-chunked-text"
            ) {
                this.responseText = xhr.responseText;
            }
            this.url = xhr.responseURL;
        } else if (xhr && typeof xhr.status === "number") {
            this.status = xhr.status;
            this.responseText = responseText;
        }
        this.callTreeId = callTreeId;
        this.retries = retries || 0;
    }
}

const errorWrapper = function (deferred: any, callTreeId: string) {
    return function (this: any, error: any, url: string) {
        /*
         * This is an ugly workaround because Qwest always throws an error on an empty response.
         * This is fixed in Version 2.0.5, so when upgrading or getting rid of it. Remove it.
         */
        if (this.status === 204) {
            deferred.resolve();
        } else {
            const returnError = new RequestError(error ? error : url, callTreeId, this);

            deferred.reject(returnError);
        }
    };
};

const getPromise = function (
    endPoint: string,
    requestParams:
        | {
              [x: string]: any;
          }
        | undefined
        | null,
    headerParam: {
        [x: string]: any;
    },
    skipCallTreeHeader?: boolean | undefined
) {
    const deferred = Q.defer(); // Have to do this, because Quests' promises are not compatible with Q.
    const header = _.assign({ timeout: 30000 }, headerParam);
    let callTreeId;
    if (skipCallTreeHeader) {
        callTreeId = null;
    } else {
        header.headers = makeCallTreeHeader();
        callTreeId = header.headers.CallTreeId;
    }

    Qwest.get(endPoint, requestParams, header).then(successWrapper(deferred)).catch(errorWrapper(deferred, callTreeId));

    return deferred.promise;
};

function withMonitoring<T>(requestData: RequestParams, promiseRequest: Promise<T>): Promise<T> {
    const startTime = new Date();
    // Promise.finally is not supported in all browsers / node, so we have to do it ourselves:
    function promiseFinally<T>(p: Promise<T>, cb: () => void): Promise<T> {
        const fin = () => Promise.resolve(cb()).then(() => p);
        return p.then(fin, fin);
    }
    return promiseFinally(promiseRequest, () => {
        try {
            const endTime = new Date();
            const loadTime = endTime.getTime() - startTime.getTime();
            PerformanceMeasureActions.apiDataLoad(
                requestData.vertical,
                requestData.service,
                requestData.template,
                loadTime
            );
        } catch (error) {
            // do nothing, this should not lead to problems if monitoring fails.
            console.warn("Monitoring failed for request: ", requestData);
        }
    });
}

async function fetchRequest(requestData: RequestParams): Promise<unknown> {
    const headers = Object.assign(
        makeCallTreeHeader(),
        requestData.requestParams ? { "Content-Type": "application/json" } : {},
        requestData.headerParams ? requestData.headerParams : {}
    );
    // Build request
    const url = buildRequest(
        requestData.vertical,
        requestData.service,
        requestData.version,
        requestData.template,
        requestData.pathParams,
        _.assign({}, requestData.queryParams, getTimestampForQueryAsObject()),
        requestData.manualBaseUrl
    );
    const callTreeId = headers.CallTreeId;
    const request: RequestInit = {
        method: requestData.httpMethod,
        headers,
        mode: "same-origin",
        credentials: "include"
    };
    if (requestData.requestParams) {
        request.body = JSON.stringify(requestData.requestParams);
    }
    let retries = 0;
    while (true) {
        try {
            const r = await fetch(url, request);
            if (r.ok) {
                // because 204 has an empty body it can't be parsed to json
                if (r.status === 204) {
                    return Promise.resolve();
                }
                return await r.json();
            } else {
                const text = await r.text();
                throw new RequestError(`HTTP Request failed with status ${r.status}`, callTreeId, r, text, retries);
            }
        } catch (ex) {
            let e: any = ex;
            if (!e._isRequestError) {
                e = new RequestError(e, callTreeId, null, undefined, retries);
            }

            await requestData.retryPolicy(e);

            retries++;
        }
    }
}

function promiseWithMonitoring(requestData: RequestParams): Promise<unknown> {
    return withMonitoring(requestData, fetchRequest(requestData));
}

const postPromise = function (
    endPoint: string,
    postObject: any,
    headerParam?: {
        [x: string]: any;
    }
) {
    const deferred = Q.defer();

    const header = _.assign({ timeout: 30000, headers: makeCallTreeHeader(), dataType: "json" }, headerParam);
    const callTreeId = header.headers.CallTreeId;

    Qwest.post(endPoint, postObject, header).then(successWrapper(deferred)).catch(errorWrapper(deferred, callTreeId));
    return deferred.promise;
};

const deletePromise = function (
    endPoint: string,
    requestParams:
        | {
              [x: string]: any;
          }
        | undefined
        | null,
    headerParam: {
        [x: string]: any;
    }
) {
    const deferred = Q.defer();

    const header = _.assign({ timeout: 30000, headers: makeCallTreeHeader(), dataType: "json" }, headerParam);
    const callTreeId = header.headers.CallTreeId;

    Qwest.delete(endPoint, requestParams, header)
        .then(successWrapper(deferred))
        .catch(errorWrapper(deferred, callTreeId));

    return deferred.promise;
};

const putPromise = function (
    endPoint: string,
    requestParams:
        | {
              [x: string]: any;
          }
        | undefined
        | null,
    headerParam: {
        [x: string]: any;
    }
) {
    const deferred = Q.defer();

    const header = _.assign({ timeout: 30000, headers: makeCallTreeHeader(), dataType: "json" }, headerParam);
    const callTreeId = header.headers.CallTreeId;

    Qwest.put(endPoint, requestParams, header).then(successWrapper(deferred)).catch(errorWrapper(deferred, callTreeId));

    return deferred.promise;
};

const Requests = {
    get: getPromise,
    post: postPromise,
    delete: deletePromise,
    put: putPromise,
    Request: RequestParams
};

export default Requests;
export { getPromise as get, postPromise as post, deletePromise as delete, putPromise as put, RequestParams as Request };
